import { useCallback } from "react";
import { hasRichTextContent } from "../utils/richTextHelpers";

export default function useBefundbeispieleOperations({
  isAdmin,
  befundbeispiele,
  setBefundbeispiele,
  persistUserBefundbeispiele,
  cloneData,
  supabase,
  tableNames,
  loadAllBefundbeispiele,
  showStatus,
  formatSupabaseError,
  createTimeBasedId,
  templateDraftName,
  templateDraftSection,
  editingId,
  setTemplateDraftName,
  setTemplateDraftSection,
  setEditingId,
  fields,
  saveTemplateInFlightRef,
  setIsSavingTemplate,
  newTplSection,
  newTplCategory,
  newSectionName,
  newSectionMod,
  newTplName,
  newTplBefund,
  newTplBeurteilung,
  newTplMod,
  setNewTplFormOpen,
  setNewTplMod,
  setNewTplSection,
  setNewTplCategory,
  setNewSectionName,
  setNewTplName,
  setNewTplBefund,
  setNewTplBeurteilung,
  saveNewTemplateInFlightRef,
  setIsSavingNewTemplate,
  normalizeGroupName,
  findExistingGroupName,
  sortGroupNames,
  defaultBefundGroups,
  customBefundGroups,
  setCustomBefundGroups,
  persistCustomBefundGroups,
  updateBefundGroupOrder,
  availableBefundGroups,
  setExpandedSections,
  setNewSectionMod,
  deleteConfirm,
  setDeleteConfirm,
  sectionDeleteConfirm,
  setSectionDeleteConfirm,
  tableNameKey = "befundbeispiele",
  itemLabel = "Befundbeispiel",
}) {
  const tableName = tableNames?.[tableNameKey];

  const doSaveTemplate = useCallback(async () => {
    if (!templateDraftName.trim()) { showStatus(`Bitte ${itemLabel}-Name eingeben.`, true); return; }
    if (!templateDraftSection.trim()) { showStatus("Bitte Sektion eingeben.", true); return; }
    if (!hasRichTextContent(fields.befund)) { showStatus("Befund darf nicht leer sein.", true); return; }
    if (saveTemplateInFlightRef.current) return;

    saveTemplateInFlightRef.current = true;
    setIsSavingTemplate(true);
    const modGroup = fields.modalitat === "CT" ? "CT" : "MRT";
    const tplData = { name: templateDraftName.trim(), section: templateDraftSection.trim(), ...fields };

    try {
      if (!isAdmin) {
        setBefundbeispiele((prev) => {
          const next = cloneData(prev);
          if (!next[modGroup]) next[modGroup] = {};
          if (!next[modGroup][templateDraftSection.trim()]) next[modGroup][templateDraftSection.trim()] = [];

          if (editingId) {
            for (const sectionName of Object.keys(next[modGroup])) {
              const index = next[modGroup][sectionName].findIndex((entry) => entry.id === editingId);
              if (index !== -1) {
                next[modGroup][sectionName].splice(index, 1);
                break;
              }
            }
            next[modGroup][templateDraftSection.trim()].unshift({ ...tplData, id: editingId, mod_group: modGroup, createdAt: Date.now() });
            showStatus(`${itemLabel} aktualisiert ✓`);
          } else {
            const id = createTimeBasedId();
            next[modGroup][templateDraftSection.trim()].unshift({ ...tplData, id, mod_group: modGroup, createdAt: Date.now() });
            showStatus(`${itemLabel} gespeichert ✓`);
          }

          persistUserBefundbeispiele(next);
          return next;
        });
        setTemplateDraftName("");
        setTemplateDraftSection("");
        setEditingId(null);
        return;
      }

      if (editingId) {
        const { error } = await supabase.from(tableName).update({ name: templateDraftName.trim(), section: templateDraftSection.trim(), mod_group: modGroup, data: tplData }).eq("id", editingId);
        if (error) throw error;
        showStatus(`${itemLabel} aktualisiert ✓`);
      } else {
        const id = Date.now().toString();
        const { error } = await supabase.from(tableName).insert({ id, name: templateDraftName.trim(), section: templateDraftSection.trim(), mod_group: modGroup, data: tplData, created_at: Date.now() });
        if (error) throw error;
        showStatus(`${itemLabel} gespeichert ✓`);
      }
      await loadAllBefundbeispiele();
      setTemplateDraftName("");
      setTemplateDraftSection("");
      setEditingId(null);
    } catch (err) {
      showStatus(`Fehler beim Speichern: ${formatSupabaseError(err)}`, true);
    } finally {
      saveTemplateInFlightRef.current = false;
      setIsSavingTemplate(false);
    }
  }, [cloneData, createTimeBasedId, editingId, fields, formatSupabaseError, isAdmin, itemLabel, loadAllBefundbeispiele, persistUserBefundbeispiele, saveTemplateInFlightRef, setBefundbeispiele, setEditingId, setIsSavingTemplate, setTemplateDraftName, setTemplateDraftSection, showStatus, supabase, tableName, templateDraftName, templateDraftSection]);

  const confirmSaveTemplate = useCallback(() => {
    void doSaveTemplate();
  }, [doSaveTemplate]);

  const doSaveNewTemplate = useCallback(async () => {
    if (saveNewTemplateInFlightRef.current) return;
    saveNewTemplateInFlightRef.current = true;
    setIsSavingNewTemplate(true);
    const sectionName = (newTplSection || newSectionName).trim();
    const normalizedMod = normalizeGroupName(newTplMod);

    try {
      if (
        normalizedMod &&
        !findExistingGroupName(defaultBefundGroups, normalizedMod) &&
        !findExistingGroupName(customBefundGroups, normalizedMod)
      ) {
        const nextGroups = sortGroupNames([...customBefundGroups, normalizedMod], defaultBefundGroups)
          .filter((group) => !findExistingGroupName(defaultBefundGroups, group));
        setCustomBefundGroups(nextGroups);
        persistCustomBefundGroups(nextGroups);
        updateBefundGroupOrder((prev) => [
          ...(prev.length ? prev : availableBefundGroups),
          normalizedMod,
        ]);
      }

      if (!isAdmin) {
        const tplData = { name: newTplName.trim(), section: sectionName, category: newTplCategory.trim(), befund: newTplBefund.trim(), beurteilung: newTplBeurteilung.trim() };

        setBefundbeispiele((prev) => {
          const next = cloneData(prev);
          const mod = normalizedMod;
          if (!next[mod]) next[mod] = {};
          if (!next[mod][sectionName]) next[mod][sectionName] = [];
          next[mod][sectionName].unshift({ ...tplData, id: `${createTimeBasedId()}-${Math.random()}`, mod_group: mod, createdAt: Date.now() });
          persistUserBefundbeispiele(next);
          return next;
        });

        showStatus(`${itemLabel} gespeichert ✓`);
        setNewTplFormOpen(false);
        setNewTplMod("CT");
        setNewTplSection("");
        setNewTplCategory("");
        setNewSectionName("");
        setNewTplName("");
        setNewTplBefund("");
        setNewTplBeurteilung("");
        return;
      }

      const tplData = { name: newTplName.trim(), section: sectionName, category: newTplCategory.trim(), befund: newTplBefund.trim(), beurteilung: newTplBeurteilung.trim() };
      const modsToCreate = [normalizedMod];

      for (const mod of modsToCreate) {
        const id = Date.now().toString() + Math.random().toString();
        const { error } = await supabase.from(tableName).insert({ id, name: newTplName.trim(), section: sectionName, mod_group: mod, data: tplData, created_at: Date.now() });
        if (error) throw error;
      }

      showStatus(`${itemLabel} gespeichert ✓`);
      await loadAllBefundbeispiele();
      setNewTplFormOpen(false);
      setNewTplMod("CT");
      setNewTplSection("");
      setNewTplCategory("");
      setNewSectionName("");
      setNewTplName("");
      setNewTplBefund("");
      setNewTplBeurteilung("");
    } catch (err) {
      showStatus(`Fehler beim Speichern: ${formatSupabaseError(err)}`, true);
    } finally {
      saveNewTemplateInFlightRef.current = false;
      setIsSavingNewTemplate(false);
    }
  }, [availableBefundGroups, cloneData, createTimeBasedId, customBefundGroups, defaultBefundGroups, findExistingGroupName, formatSupabaseError, isAdmin, itemLabel, loadAllBefundbeispiele, newSectionName, newTplBefund, newTplBeurteilung, newTplCategory, newTplMod, newTplName, newTplSection, normalizeGroupName, persistCustomBefundGroups, persistUserBefundbeispiele, saveNewTemplateInFlightRef, setBefundbeispiele, setCustomBefundGroups, setIsSavingNewTemplate, setNewSectionName, setNewTplBefund, setNewTplBeurteilung, setNewTplCategory, setNewTplFormOpen, setNewTplMod, setNewTplName, setNewTplSection, showStatus, sortGroupNames, supabase, tableName, updateBefundGroupOrder]);

  const confirmSaveNewTemplate = useCallback(() => {
    const sectionForSave = (newTplSection || newSectionName).trim();
    if (!newTplName.trim()) { showStatus(`Bitte ${itemLabel}-Name eingeben.`, true); return; }
    if (!sectionForSave) { showStatus("Bitte Sektion wählen oder eingeben.", true); return; }
    if (!hasRichTextContent(newTplBefund)) { showStatus("Befund darf nicht leer sein.", true); return; }
    void doSaveNewTemplate();
  }, [doSaveNewTemplate, itemLabel, newSectionName, newTplBefund, newTplName, newTplSection, showStatus]);

  const confirmCreateSection = useCallback(() => {
    const normalizedSectionName = normalizeGroupName(newSectionName);
    const normalizedMod = normalizeGroupName(newSectionMod);

    if (!normalizedSectionName) {
      showStatus("Bitte Sektionsname eingeben.", true);
      return;
    }

    if (!normalizedMod) {
      showStatus("Bitte zuerst eine Sektion wählen.", true);
      return;
    }

    try {
      setBefundbeispiele((prev) => {
        const next = cloneData(prev);
        const mod = normalizedMod;

        if (!next[mod]) {
          next[mod] = {};
        }
        if (!next[mod][normalizedSectionName]) {
          next[mod][normalizedSectionName] = [];
        }

        persistUserBefundbeispiele(next);
        return next;
      });

      setExpandedSections((prevSections) => ({
        ...prevSections,
        [`group:${normalizedMod}`]: true,
        [`${normalizedMod}:${normalizedSectionName}`]: true,
      }));

      showStatus(`Sektion "${normalizedSectionName}" erstellt ✓`);
      setNewSectionName("");
      setNewSectionMod(normalizedMod);
      setNewTplSection(normalizedSectionName);
    } catch (err) {
      showStatus(`Fehler beim Erstellen der Sektion: ${err?.message || "Unbekannter Fehler"}`, true);
    }
  }, [cloneData, newSectionMod, newSectionName, normalizeGroupName, persistUserBefundbeispiele, setBefundbeispiele, setExpandedSections, setNewSectionMod, setNewSectionName, setNewTplSection, showStatus]);

  const confirmDeleteTemplate = useCallback((id, modGroup, section) => {
    setDeleteConfirm({ show: true, id, modGroup, section });
  }, [setDeleteConfirm]);

  const doDeleteTemplate = useCallback(async () => {
    const { id, modGroup, section } = deleteConfirm;

    if (!isAdmin) {
      setBefundbeispiele((prev) => {
        const next = cloneData(prev);
        next[modGroup][section] = (next[modGroup][section] || []).filter((t) => t.id !== id);
        persistUserBefundbeispiele(next);
        return next;
      });
      showStatus(`${itemLabel} gelöscht ✓`);
      setDeleteConfirm({ show: false, id: null, modGroup: null, section: null });
      return;
    }

    try {
      const { error } = await supabase.from(tableName).delete().eq("id", id);
      if (error) throw error;
      setBefundbeispiele((prev) => {
        const next = cloneData(prev);
        next[modGroup][section] = next[modGroup][section].filter((t) => t.id !== id);
        return next;
      });
      showStatus(`${itemLabel} gelöscht ✓`);
    } catch (err) {
      showStatus(`Fehler beim Löschen: ${formatSupabaseError(err)}`, true);
    }
    setDeleteConfirm({ show: false, id: null, modGroup: null, section: null });
  }, [cloneData, deleteConfirm, formatSupabaseError, isAdmin, itemLabel, persistUserBefundbeispiele, setBefundbeispiele, setDeleteConfirm, showStatus, supabase, tableName]);

  const confirmDeleteSection = useCallback((modGroup, section) => {
    setSectionDeleteConfirm({ show: true, modGroup, section });
  }, [setSectionDeleteConfirm]);

  const doDeleteSection = useCallback(async () => {
    const { modGroup, section } = sectionDeleteConfirm;
    const tpls = befundbeispiele[modGroup]?.[section] || [];

    if (!isAdmin) {
      setBefundbeispiele((prev) => {
        const next = cloneData(prev);
        delete next[modGroup][section];
        persistUserBefundbeispiele(next);
        return next;
      });
      showStatus("Sektion gelöscht ✓");
      setSectionDeleteConfirm({ show: false, modGroup: null, section: null });
      return;
    }

    try {
      for (const t of tpls) await supabase.from(tableName).delete().eq("id", t.id);
      setBefundbeispiele((prev) => {
        const next = cloneData(prev);
        delete next[modGroup][section];
        return next;
      });
      showStatus("Sektion gelöscht ✓");
    } catch (err) {
      showStatus(`Fehler beim Löschen: ${formatSupabaseError(err)}`, true);
    }
    setSectionDeleteConfirm({ show: false, modGroup: null, section: null });
  }, [befundbeispiele, cloneData, formatSupabaseError, isAdmin, persistUserBefundbeispiele, sectionDeleteConfirm, setBefundbeispiele, setSectionDeleteConfirm, showStatus, supabase, tableName]);

  const persistSectionOrder = useCallback(async (_modGroup, _section, list) => {
    if (!supabase) return;
    try {
      for (let i = 0; i < list.length; i += 1) {
        const tpl = list[i];
        const payload = { ...tpl };
        delete payload.id;
        delete payload.createdAt;
        const { error } = await supabase.from(tableName).update({ data: { ...payload, order_index: i } }).eq("id", tpl.id);
        if (error) throw error;
      }
    } catch (err) {
      console.error("Fehler beim Speichern der Reihenfolge:", err);
    }
  }, [supabase, tableName]);

  const persistModSectionOrder = useCallback(async (modGroup, sectionKeys, sections) => {
    if (!supabase) return;
    try {
      for (let sectionIndex = 0; sectionIndex < sectionKeys.length; sectionIndex += 1) {
        const sectionName = sectionKeys[sectionIndex];
        const list = sections?.[sectionName] || [];
        for (const tpl of list) {
          const payload = { ...tpl, section_order_index: sectionIndex };
          delete payload.id;
          delete payload.createdAt;
          const { error } = await supabase.from(tableName).update({ data: payload }).eq("id", tpl.id);
          if (error) throw error;
        }
      }
    } catch (err) {
      console.error("Fehler beim Speichern der Sektionsreihenfolge:", err);
    }
  }, [supabase, tableName]);

  const moveTemplate = useCallback(async (modGroup, section, index, direction) => {
    const next = cloneData(befundbeispiele);
    const list = next[modGroup][section];
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    for (let i = 0; i < list.length; i += 1) list[i].order_index = i;
    setBefundbeispiele(next);
    if (!isAdmin) {
      persistUserBefundbeispiele(next);
      return;
    }
    await persistSectionOrder(modGroup, section, list);
  }, [befundbeispiele, cloneData, isAdmin, persistSectionOrder, persistUserBefundbeispiele, setBefundbeispiele]);

  const moveSection = useCallback((modGroup, sectionIndex, direction) => {
    const next = cloneData(befundbeispiele);
    const sectionKeys = Object.keys(next[modGroup]);
    const targetIndex = direction === "up" ? sectionIndex - 1 : sectionIndex + 1;
    if (targetIndex < 0 || targetIndex >= sectionKeys.length) return;

    [sectionKeys[sectionIndex], sectionKeys[targetIndex]] = [sectionKeys[targetIndex], sectionKeys[sectionIndex]];

    const reorderedSections = {};
    sectionKeys.forEach((key, index) => {
      reorderedSections[key] = (next[modGroup][key] || []).map((entry) => ({
        ...entry,
        section_order_index: index,
      }));
    });
    next[modGroup] = reorderedSections;

    setBefundbeispiele(next);
    if (!isAdmin) {
      persistUserBefundbeispiele(next);
      showStatus("Sektion verschoben ✓");
      return;
    }
    void persistModSectionOrder(modGroup, sectionKeys, reorderedSections);
    showStatus("Sektion verschoben ✓");
  }, [befundbeispiele, cloneData, isAdmin, persistModSectionOrder, persistUserBefundbeispiele, setBefundbeispiele, showStatus]);

  const renameBefundSection = useCallback(async (modGroup, sectionName, befundEntries, nextSectionName) => {
    const normalizedName = String(nextSectionName || "").trim();

    if (!normalizedName) {
      showStatus("Bitte Sektion eingeben.", true);
      return false;
    }

    if (!modGroup || !sectionName) {
      showStatus("Sektion konnte nicht eindeutig zugeordnet werden.", true);
      return false;
    }

    const currentBefundEntries = Array.isArray(befundEntries)
      ? befundEntries
      : (befundbeispiele[modGroup]?.[sectionName] || []);

    if (currentBefundEntries.length === 0) {
      setBefundbeispiele((prev) => {
        const next = cloneData(prev);
        if (!next[modGroup]) next[modGroup] = {};
        const targetEntries = sectionName === normalizedName
          ? (next[modGroup][sectionName] || [])
          : (next[modGroup][normalizedName] || []);

        next[modGroup][normalizedName] = [...targetEntries];
        if (sectionName !== normalizedName) {
          delete next[modGroup][sectionName];
        }

        if (!isAdmin) persistUserBefundbeispiele(next);
        return next;
      });
      showStatus("Sektion aktualisiert ✓");
      return true;
    }

    if (!isAdmin) {
      try {
        const updatedBefundEntries = currentBefundEntries.map((tpl) => ({ ...tpl, section: normalizedName }));
        const updatedIds = new Set(updatedBefundEntries.map((tpl) => tpl.id));

        setBefundbeispiele((prev) => {
          const next = cloneData(prev);
          if (!next[modGroup]) next[modGroup] = {};
          const targetEntries = sectionName === normalizedName
            ? []
            : (next[modGroup][normalizedName] || []).filter((tpl) => !updatedIds.has(tpl.id));

          next[modGroup][normalizedName] = [...updatedBefundEntries, ...targetEntries];
          if (sectionName !== normalizedName) {
            delete next[modGroup][sectionName];
          }

          persistUserBefundbeispiele(next);
          return next;
        });
        showStatus("Sektion aktualisiert ✓");
        return true;
      } catch (err) {
        showStatus(`Fehler beim Aktualisieren: ${formatSupabaseError(err)}`, true);
        return false;
      }
    }

    try {
      const payloads = currentBefundEntries.map((tpl) => {
        const payload = { ...tpl, section: normalizedName };
        delete payload.id;
        delete payload.createdAt;
        delete payload.mod_group;
        return {
          id: tpl.id,
          name: tpl.name || "",
          section: normalizedName,
          mod_group: modGroup,
          data: payload,
        };
      });

      const results = await Promise.all(
        payloads.map((payload) => (
          supabase
            .from(tableName)
            .update({
              name: payload.name,
              section: payload.section,
              mod_group: payload.mod_group,
              data: payload.data,
            })
            .eq("id", payload.id)
        ))
      );
      const failed = results.find((result) => result.error);
      if (failed?.error) throw failed.error;

      await loadAllBefundbeispiele();
      showStatus("Sektion aktualisiert ✓");
      return true;
    } catch (err) {
      showStatus(`Fehler beim Aktualisieren: ${formatSupabaseError(err)}`, true);
      return false;
    }
  }, [befundbeispiele, cloneData, formatSupabaseError, isAdmin, loadAllBefundbeispiele, persistUserBefundbeispiele, setBefundbeispiele, showStatus, supabase, tableName]);

  const deleteBefundSection = useCallback(async (modGroup, sectionName, befundEntries) => {
    if (!modGroup || !sectionName) {
      showStatus("Sektion konnte nicht eindeutig zugeordnet werden.", true);
      return false;
    }

    const currentBefundEntries = Array.isArray(befundEntries)
      ? befundEntries
      : (befundbeispiele[modGroup]?.[sectionName] || []);

    if (currentBefundEntries.length === 0) {
      setBefundbeispiele((prev) => {
        const next = cloneData(prev);
        if (next[modGroup]) {
          delete next[modGroup][sectionName];
        }
        if (!isAdmin) persistUserBefundbeispiele(next);
        return next;
      });
      showStatus("Leere Sektion entfernt ✓");
      return true;
    }

    if (!isAdmin) {
      try {
        setBefundbeispiele((prev) => {
          const next = cloneData(prev);
          if (next[modGroup]) {
            delete next[modGroup][sectionName];
          }
          persistUserBefundbeispiele(next);
          return next;
        });
        showStatus("Sektion gelöscht ✓");
        return true;
      } catch (err) {
        showStatus(`Fehler beim Löschen: ${formatSupabaseError(err)}`, true);
        return false;
      }
    }

    try {
      const results = await Promise.all(
        currentBefundEntries.map((tpl) => supabase.from(tableName).delete().eq("id", tpl.id))
      );
      const failed = results.find((result) => result.error);
      if (failed?.error) throw failed.error;

      await loadAllBefundbeispiele();
      showStatus("Sektion gelöscht ✓");
      return true;
    } catch (err) {
      showStatus(`Fehler beim Löschen: ${formatSupabaseError(err)}`, true);
      return false;
    }
  }, [befundbeispiele, cloneData, formatSupabaseError, isAdmin, loadAllBefundbeispiele, persistUserBefundbeispiele, setBefundbeispiele, showStatus, supabase, tableName]);

  const saveBefundbeispielInline = useCallback(async (tpl) => {
    if (!tpl?.id) { showStatus(`Ungültiges ${itemLabel}.`, true); return false; }
    if (!(tpl.name || "").trim()) { showStatus("Bitte Name eingeben.", true); return false; }
    if (!(tpl.section || "").trim()) { showStatus("Bitte Sektion angeben.", true); return false; }
    if (!hasRichTextContent(tpl.befund)) { showStatus("Befund darf nicht leer sein.", true); return false; }

    const modGroup = tpl.mod_group || tpl.modalitat || "Sonstiges";
    const payload = { ...tpl };
    delete payload.id;
    delete payload.createdAt;
    delete payload.mod_group;

    if (!isAdmin) {
      const next = cloneData(befundbeispiele);
      const sectionList = next[modGroup]?.[tpl.section] || [];
      const index = sectionList.findIndex((entry) => entry.id === tpl.id);
      if (index === -1) {
        showStatus(`${itemLabel} nicht gefunden.`, true);
        return false;
      }
      sectionList[index] = { ...tpl, mod_group: modGroup };
      setBefundbeispiele(next);
      persistUserBefundbeispiele(next);
      showStatus(`${itemLabel} aktualisiert ✓`);
      return true;
    }

    try {
      const { error } = await supabase
        .from(tableName)
        .update({
          name: tpl.name || "",
          section: tpl.section || "",
          mod_group: modGroup,
          data: payload,
        })
        .eq("id", tpl.id);
      if (error) throw error;
      await loadAllBefundbeispiele();
      showStatus(`${itemLabel} aktualisiert ✓`);
      return true;
    } catch (err) {
      showStatus(`Fehler beim Aktualisieren: ${formatSupabaseError(err)}`, true);
      return false;
    }
  }, [befundbeispiele, cloneData, formatSupabaseError, isAdmin, itemLabel, loadAllBefundbeispiele, persistUserBefundbeispiele, setBefundbeispiele, showStatus, supabase, tableName]);

  return {
    confirmSaveTemplate,
    confirmSaveNewTemplate,
    confirmCreateSection,
    confirmDeleteTemplate,
    doDeleteTemplate,
    confirmDeleteSection,
    doDeleteSection,
    moveTemplate,
    moveSection,
    renameBefundSection,
    deleteBefundSection,
    saveBefundbeispielInline,
  };
}
